import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'generaldate',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './general-date.component.html',
  styleUrls: ['./general-date.component.scss']
})
export class GeneralDateComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() controlName!: string;
  @Input() label: string = '';
  @Input() alertMessage: string = '';
  @Input() placeholder: string = '';
  @Input() isReadOnly: boolean = false;
  @Input() minDate: string = this.get100YearsAgo().toISOString().split('T')[0]; // 100 years ago
  @Input() maxDate: string = '';
  @Input() allowFutureDates: boolean = false;
  focusedControls: { [key: string]: boolean } = {};
  private dateInput$ = new Subject<string>();
 
  constructor() { }

  ngOnInit(): void {
    if (!this.form) {
      console.error('Form is required for generaldate component');
    }
    if (!this.controlName) {
      console.error('ControlName is required for generaldate component');
    }


    this.dateInput$
    .pipe(debounceTime(1200)) // Espera 400ms tras la última tecla
    .subscribe((inputValue) => this.validateDateInput(inputValue));

    // Agregar validador de fecha futura si no se permiten fechas futuras
    if (!this.allowFutureDates) {
      const control = this.form.get(this.controlName);
      if (control) {
        // Obtener validadores existentes
        const existingValidators = control.validator;

        // Combinar con el nuevo validador
        const validators = existingValidators
          ? [existingValidators, this.futureDateValidator()]
          : [this.futureDateValidator()];

        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    }

    // Establecer maxDate como hoy si no se permiten fechas futuras y no se ha establecido maxDate
    if (!this.allowFutureDates && !this.maxDate) {
      this.maxDate = this.getTodayString();
    }

    // Agregar validador de rango si minDate y maxDate están definidos
    if (this.minDate && this.maxDate) {
      const control = this.form.get(this.controlName);
      if (control) {
        const existingValidators = control.validator;
        const rangeValidator = this.dateRangeValidator(this.minDate, this.maxDate);
        const validators = existingValidators ? [existingValidators, rangeValidator] : [rangeValidator];
        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    }
  }

  // Validador personalizado para fechas futuras
  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // No validar si no hay valor
      }

      const inputDate = control.value;
      const today = this.getTodayString();


      if (inputDate > today) {
        return { futureDate: { actualDate: control.value, maxDate: this.getTodayString() } };
      }

      return null;
    };
  }

  // Validador personalizado para rango de fechas
  dateRangeValidator(min: string, max: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // No validar si no hay valor
      }

      const inputDate = control.value;
      if (inputDate < min || inputDate > max) {
        return { dateRange: { min, max, actual: inputDate } };
      }

      return null;
    };
  }

  // Obtener la fecha de hoy en formato YYYY-MM-DD
  private getTodayString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  
  onDateChange(event: Event): void {
    if (!this.allowFutureDates) {
      const input = event.target as HTMLInputElement;
      const inputValue = input.value;
      this.dateInput$.next(inputValue);
    }
  }
  

  onKeyUp(event: KeyboardEvent): void {
  if (!this.allowFutureDates) {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value;
    this.dateInput$.next(inputValue);
  }
}

  get isFutureDate() {
    return this.form.get(this.controlName)?.errors?.['futureDate'] &&
      this.form.get(this.controlName)?.touched;
  }

  get isDateRangeError() {
    return this.form.get(this.controlName)?.errors?.['dateRange'] &&
      this.form.get(this.controlName)?.touched;
  }

  get isInvalid() {
    return this.form.get(this.controlName)?.invalid &&
      this.form.get(this.controlName)?.touched;
  }

  get isRequired() {
    return this.form.get(this.controlName)?.errors?.['required'] &&
      this.form.get(this.controlName)?.touched;
  }

  get isInvalidDate() {
    return this.form.get(this.controlName)?.errors?.['matDatepickerParse'] &&
      this.form.get(this.controlName)?.touched;
  }

  onFocus() {
    this.focusedControls[this.controlName] = true; // Set focus state to true
  }

  onBlur() {
    this.focusedControls[this.controlName] = false; // Set focus state to false
    // Reset value if date is out of range
    if (this.isDateRangeError) {
      this.form.get(this.controlName)?.setValue('');
      this.form.get(this.controlName)?.markAsDirty();
    }
  }

  isFocused(controlName: string): boolean {
    return !!this.focusedControls[controlName]; // Return focus state
  }

  public isValidField(field: string) {
    const control = this.form.get(field);
    return control && control.errors && (control.dirty || control.touched);
  }


  private get100YearsAgo() {
    const today = new Date();
    const hundredYearsAgo = new Date(today);

    // Restar 100 años
    hundredYearsAgo.setFullYear(today.getFullYear() - 100);

    return hundredYearsAgo;
  }

  private validateDateInput(inputValue: string): void {
  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(inputValue);

  if (inputValue && inputValue.length === 10 && isValidFormat) {
    const today = this.getTodayString();
    const oldDate = this.minDate;

    if (inputValue < oldDate || inputValue > today) {
      const fallback = today;
      this.form.get(this.controlName)?.setValue('');
      this.form.get(this.controlName)?.markAsDirty();
    }
  }
}


} 